import { z } from "zod";
import {
  availableLivestreamProviderNames,
  isLivestreamAddress,
  isLivestreamProviderAvailable,
  LIVESTREAM_PROVIDER_KEYS,
  LIVESTREAM_PROVIDER_LABELS,
  YOUTUBE_CHANNEL_ID,
  youtubeHandle,
} from "./livestream-sources";

/**
 * Block content contracts (ADR-0007, ADR 0031, ADR 0033): what a block *says*, never how a renderer draws it. Every
 * block carries a stable `type` and a `variant` the theme may honor. Text is plain or structured rich text, never
 * HTML; links are site paths, https, mailto, tel or a same-page anchor; images live in the church's public media area.
 *
 * The nineteen types are architecture section 11.4's eighteen and the Live now block (ADR 0033, amendment of
 * 2026-10-03). `private.is_block_type` in the latest migration that defines it lists the same nineteen, and a test
 * compares the two.
 */

// ------------------------------------------------------------------------------------------------ building blocks

const text = (max: number) => z.string().trim().min(1).max(max);

/** A link a church may place on its site. `javascript:` and every other scheme are refused. */
export const linkSchema = z.object({
  label: text(80),
  href: z
    .string()
    .min(1)
    .max(500)
    .regex(/^(\/|#|https:\/\/|mailto:|tel:)/, "Links start with /, #, https://, mailto: or tel:"),
});

/**
 * An image in the church's public media area (ADR 0021): `<church id>/media/<file>`. Alt text is required unless the
 * image is marked decorative, in which case the renderer gives it an empty alt so screen readers skip it.
 */
export const imageSchema = z
  .object({
    path: z
      .string()
      .max(500)
      .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/media\/.+/),
    alt: z.string().trim().max(200).default(""),
    decorative: z.boolean().default(false),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
  })
  .refine((image) => image.decorative || image.alt.length > 0, {
    message: "An image needs alt text unless it is decorative",
    path: ["alt"],
  });

/** A run of text with optional emphasis and an optional link. */
export const inlineSchema = z.object({
  text: z.string().min(1).max(2000),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  href: linkSchema.shape.href.optional(),
});

const inlines = z.array(inlineSchema).min(1).max(50);

/**
 * Rich text as structured nodes, not HTML (ADR 0033): paragraphs, second- and third-level headings (the page's first
 * heading belongs to the page), bulleted or numbered lists, and quotes. A renderer can never be handed markup to inject.
 */
export const richTextSchema = z
  .array(
    z.discriminatedUnion("type", [
      z.object({ type: z.literal("paragraph"), content: inlines }),
      z.object({
        type: z.literal("heading"),
        level: z.union([z.literal(2), z.literal(3)]),
        content: inlines,
      }),
      z.object({
        type: z.literal("list"),
        ordered: z.boolean().default(false),
        items: z.array(inlines).min(1).max(50),
      }),
      z.object({ type: z.literal("quote"), content: inlines, cite: text(120).optional() }),
    ]),
  )
  .min(1)
  .max(100);

const id = z.uuid();
const days = z.enum(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]);

// ------------------------------------------------------------------------------------------------ the nineteen

export const heroBlockSchema = z.object({
  type: z.literal("hero"),
  variant: z.enum(["standard", "split", "image_background"]).default("standard"),
  headline: text(160),
  subheadline: z.string().max(300).optional(),
  primaryAction: linkSchema.optional(),
  secondaryAction: linkSchema.optional(),
  image: imageSchema.optional(),
});

export const serviceTimesBlockSchema = z.object({
  type: z.literal("service_times"),
  variant: z.enum(["list", "cards"]).default("list"),
  heading: text(120),
  services: z
    .array(
      z.object({
        name: text(80),
        day: days,
        /** Local time in HH:MM (24 h); renderers format it for the church's locale. */
        time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        note: z.string().max(160).optional(),
      }),
    )
    .min(1)
    .max(12),
});

export const locationsBlockSchema = z.object({
  type: z.literal("locations"),
  variant: z.enum(["list", "map"]).default("list"),
  heading: text(120),
  locations: z
    .array(
      z.object({
        name: text(120),
        addressLines: z.array(text(120)).min(1).max(4),
        phone: z.string().max(40).optional(),
        mapUrl: z.url({ protocol: /^https$/ }).optional(),
      }),
    )
    .min(1)
    .max(20),
});

export const missionBlockSchema = z.object({
  type: z.literal("mission"),
  variant: z.enum(["statement", "values"]).default("statement"),
  heading: text(120),
  statement: text(600),
  values: z
    .array(z.object({ title: text(80), description: z.string().max(300).optional() }))
    .max(8)
    .default([]),
});

/**
 * Sermons come from the sermons and media module (ADR 0061); the block chooses which: the latest, a series, or one
 * sermon. `latest` draws one sermon as a card with a link to it, `featured` draws it large with its video in the page,
 * and `list` draws up to `limit` of them. Every variant links to the church's sermon archive.
 */
export const sermonFeatureBlockSchema = z.object({
  type: z.literal("sermon_feature"),
  variant: z.enum(["latest", "featured", "list"]).default("latest"),
  heading: text(120),
  source: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("latest") }),
    z.object({ mode: z.literal("series"), seriesId: id }),
    z.object({ mode: z.literal("sermon"), sermonId: id }),
  ]),
  showSpeaker: z.boolean().default(true),
  /** How many sermons the list variant draws. */
  limit: z.number().int().min(1).max(12).default(3),
});

/** Upcoming events from the events module, filtered by the church's own categories. */
export const eventListingBlockSchema = z.object({
  type: z.literal("event_listing"),
  variant: z.enum(["list", "cards", "calendar"]).default("list"),
  heading: text(120),
  categoryIds: z.array(id).max(10).default([]),
  limit: z.number().int().min(1).max(12).default(6),
  emptyMessage: z.string().max(200).optional(),
});

/** A searchable list of the groups a church makes public, from the groups module. */
export const groupFinderBlockSchema = z.object({
  type: z.literal("group_finder"),
  variant: z.enum(["grid", "list"]).default("grid"),
  heading: text(120),
  filters: z
    .array(z.enum(["day", "location", "category", "age"]))
    .max(4)
    .default([]),
  categoryIds: z.array(id).max(10).default([]),
});

export const ministryGridBlockSchema = z.object({
  type: z.literal("ministry_grid"),
  variant: z.enum(["grid", "list"]).default("grid"),
  heading: text(120),
  items: z
    .array(
      z.object({
        title: text(80),
        description: z.string().max(300).optional(),
        image: imageSchema.optional(),
        link: linkSchema.optional(),
      }),
    )
    .min(1)
    .max(24),
});

/**
 * Staff as the church chooses to present them. Entered here rather than drawn from the CRM, so publishing a staff
 * page never exposes a person's record, and a person appears on the website only because someone put them there.
 */
export const staffGridBlockSchema = z.object({
  type: z.literal("staff_grid"),
  variant: z.enum(["grid", "list"]).default("grid"),
  heading: text(120),
  people: z
    .array(
      z.object({
        name: text(120),
        role: text(120),
        bio: z.string().max(600).optional(),
        image: imageSchema.optional(),
        email: z.email().optional(),
      }),
    )
    .min(1)
    .max(48),
});

/** Points to where a church takes gifts; online giving itself arrives with commerce (Milestone 4). */
export const givingBlockSchema = z.object({
  type: z.literal("giving"),
  variant: z.enum(["simple", "split"]).default("simple"),
  heading: text(120),
  message: z.string().max(600).optional(),
  action: linkSchema,
  image: imageSchema.optional(),
});

export const storeCollectionBlockSchema = z.object({
  type: z.literal("store_collection"),
  variant: z.enum(["grid", "carousel"]).default("grid"),
  heading: text(120),
  /*
   * Store collections are not built yet (ADR 0071): the block draws the Shop's products in the church's order, up to
   * `limit`. A stored collection id is kept for when they are.
   */
  collectionId: id.optional(),
  limit: z.number().int().min(1).max(24).default(8),
});

/** A sign-up for the church's newsletter, through a public form (Milestone 2 task 13). */
export const newsletterBlockSchema = z.object({
  type: z.literal("newsletter"),
  variant: z.enum(["inline", "card"]).default("inline"),
  heading: text(120),
  description: z.string().max(300).optional(),
  formId: id,
});

export const contactFormBlockSchema = z.object({
  type: z.literal("contact_form"),
  variant: z.enum(["standard", "split"]).default("standard"),
  heading: text(120),
  description: z.string().max(300).optional(),
  formId: id,
});

export const planVisitBlockSchema = z.object({
  type: z.literal("plan_visit"),
  variant: z.enum(["standard", "steps"]).default("standard"),
  heading: text(120),
  intro: z.string().max(600).optional(),
  steps: z
    .array(z.object({ title: text(80), description: z.string().max(300).optional() }))
    .max(6)
    .default([]),
  action: linkSchema.optional(),
});

export const richTextBlockSchema = z.object({
  type: z.literal("rich_text"),
  variant: z.enum(["standard", "narrow"]).default("standard"),
  body: richTextSchema,
});

export const faqBlockSchema = z.object({
  type: z.literal("faq"),
  variant: z.enum(["accordion", "list"]).default("accordion"),
  heading: text(120),
  items: z
    .array(z.object({ question: text(200), answer: richTextSchema }))
    .min(1)
    .max(30),
});

export const mediaGalleryBlockSchema = z.object({
  type: z.literal("media_gallery"),
  variant: z.enum(["grid", "carousel"]).default("grid"),
  heading: z.string().max(120).optional(),
  images: z
    .array(z.object({ image: imageSchema, caption: z.string().max(200).optional() }))
    .min(1)
    .max(24),
});

/**
 * Embeds from an approved list only (ADR 0033). The block stores the provider's own page address; the renderer
 * derives the embed address from it, so no one can place an arbitrary frame or script on a church's site.
 */
export const EMBED_PROVIDERS = {
  youtube: /^https:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[A-Za-z0-9_-]{6,20}/,
  vimeo: /^https:\/\/(www\.)?vimeo\.com\/\d{4,12}/,
  google_maps: /^https:\/\/(www\.)?google\.com\/maps\/embed\?pb=/,
  spotify: /^https:\/\/open\.spotify\.com\/(episode|show|playlist|track|album)\/[A-Za-z0-9]{10,30}/,
} as const;

export const customEmbedBlockSchema = z
  .object({
    type: z.literal("custom_embed"),
    variant: z.enum(["wide", "standard", "square"]).default("wide"),
    provider: z.enum(["youtube", "vimeo", "google_maps", "spotify"]),
    url: z.string().max(1000),
    /** Required: a frame without a title is unnamed to a screen reader. */
    title: text(160),
    caption: z.string().max(200).optional(),
  })
  .refine((block) => EMBED_PROVIDERS[block.provider].test(block.url), {
    message: "The address is not one this provider serves",
    path: ["url"],
  });

/** A wall-clock time in HH:MM (24 h). */
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time such as 10:00");

/**
 * The Live now block (ADR 0033, amendments of 2026-10-03): the church's stream at its service times, a countdown
 * before, and a replay link afterwards. The weekly times are wall-clock times in the church's zone, or in one campus's
 * zone when the block names a campus; the public site decides at each request, on its own clock, whether the church
 * is live now (`livestream.ts`), so the block switches at the second with no database read. Everything in it is
 * public information a church prints anyway: when it streams, and where.
 */
export const livestreamBlockSchema = z
  .object({
    type: z.literal("livestream"),
    /** `player` plays the stream in the page while live; `button` links out to the provider. */
    variant: z.enum(["player", "button"]).default("player"),
    heading: text(120),
    provider: z.enum(LIVESTREAM_PROVIDER_KEYS),
    /** The provider's own page address for the stream; the renderer derives the frame from it. */
    url: z.string().max(1000),
    /**
     * The YouTube channel a handle address (`youtube.com/@Church/live`) names, found when staff saved the block, so
     * the page never asks YouTube while a visitor waits. Required with a handle address; refused with anything else.
     */
    channelId: z
      .string()
      .regex(YOUTUBE_CHANNEL_ID, "A YouTube channel id starts with UC")
      .optional(),
    services: z
      .array(
        z.object({
          day: days,
          start: clockTime,
          /** How long the stream is live, in minutes, from `start`. */
          durationMinutes: z.number().int().min(5).max(360),
        }),
      )
      .min(1)
      .max(14),
    /**
     * Whether "Starts in 4:32" counts down before a service. On by default; a church that runs its own countdown
     * inside the stream turns it off.
     */
    countdown: z.boolean().default(true),
    /** How long before a start the countdown shows, in minutes: 1 to 10,080 (a week). */
    countdownMinutes: z.number().int().min(1).max(10_080).default(60),
    /**
     * How many minutes before each service's start the player opens (the block is live from then), as Planning
     * Center's countdown minutes "start the stream a set number of minutes before the episode's start time": 0 (at the
     * start) to 60. While it is more than 0 the countdown counts to the opening and says "Opens in 4:32" (ADR 0033,
     * third amendment of 2026-10-03).
     */
    openEarlyMinutes: z.number().int().min(0).max(60).default(0),
    /** How long "Watch the replay" shows after a service ends, in hours: 0 (never) to 168 (a week). */
    replayHours: z.number().int().min(0).max(168).default(24),
    /**
     * The campus whose time zone the services are in; absent, the church's. A campus that is not the church's, or is
     * archived, is ignored and the church's zone used: the public site only knows the church's active campuses.
     */
    campusId: z.uuid().optional(),
    /** Where "Watch the replay" goes after a service; absent, it goes to the stream's own address. */
    replayUrl: z
      .string()
      .trim()
      .max(500)
      .regex(/^(\/|https:\/\/)/, "A replay link starts with / or https://")
      .optional(),
    /** Shown while the church is not live, such as "Join us online every Sunday". */
    upcomingMessage: z.string().trim().max(300).optional(),
  })
  .superRefine((block, ctx) => {
    // Resi, BoxCast and Facebook are "Coming soon" until counsel answers (AVAILABLE_LIVESTREAM_PROVIDERS). Refused at
    // save; a block already saved with one does not parse on the public site, so it is not drawn at all: no
    // third-party player and no link to one reaches a visitor before the owner decides.
    if (!isLivestreamProviderAvailable(block.provider)) {
      ctx.addIssue({
        code: "custom",
        message: `${LIVESTREAM_PROVIDER_LABELS[block.provider]} streams are coming soon. For now, choose ${availableLivestreamProviderNames()}.`,
        path: ["provider"],
      });
    }
  })
  .refine((block) => isLivestreamAddress(block.provider, block.url), {
    message: `The address is not a stream this provider serves. Paste the stream's address from ${availableLivestreamProviderNames()}.`,
    path: ["url"],
  })
  .refine(
    (block) =>
      block.provider !== "youtube" ||
      youtubeHandle(block.url) === null ||
      block.channelId !== undefined,
    {
      message:
        "We have not found the channel for this YouTube handle yet. Press Find channel, or paste the channel's address (https://www.youtube.com/channel/UC…).",
      path: ["url"],
    },
  )
  .refine(
    (block) =>
      block.channelId === undefined ||
      (block.provider === "youtube" && youtubeHandle(block.url) !== null),
    { message: "A channel id goes only with a YouTube handle address", path: ["channelId"] },
  );

// ------------------------------------------------------------------------------------------------ the union

export const blockSchema = z.discriminatedUnion("type", [
  heroBlockSchema,
  serviceTimesBlockSchema,
  locationsBlockSchema,
  missionBlockSchema,
  sermonFeatureBlockSchema,
  eventListingBlockSchema,
  groupFinderBlockSchema,
  ministryGridBlockSchema,
  staffGridBlockSchema,
  givingBlockSchema,
  storeCollectionBlockSchema,
  newsletterBlockSchema,
  contactFormBlockSchema,
  planVisitBlockSchema,
  richTextBlockSchema,
  faqBlockSchema,
  mediaGalleryBlockSchema,
  customEmbedBlockSchema,
  livestreamBlockSchema,
]);

export type Block = z.infer<typeof blockSchema>;
export type BlockType = Block["type"];
export type BlockOf<T extends BlockType> = Extract<Block, { type: T }>;
export type HeroBlock = BlockOf<"hero">;
export type ServiceTimesBlock = BlockOf<"service_times">;
export type LocationsBlock = BlockOf<"locations">;
export type GroupFinderBlock = BlockOf<"group_finder">;
export type EventListingBlock = BlockOf<"event_listing">;
export type LivestreamBlock = BlockOf<"livestream">;
export type SermonFeatureBlock = BlockOf<"sermon_feature">;
export type RichText = z.infer<typeof richTextSchema>;
export type Image = z.infer<typeof imageSchema>;
export type Link = z.infer<typeof linkSchema>;

/**
 * What the editor and the renderer need to know about each type besides its shape: a name staff recognise, what it is
 * for, and the module whose data it shows (null when the block's content is entirely its own). A block whose module
 * the church does not have is not offered in the editor and not rendered.
 */
export interface BlockCatalogEntry {
  readonly type: BlockType;
  readonly label: string;
  readonly description: string;
  readonly module: "sermons_media" | "events" | "groups" | "commerce" | null;
  /** Content the block cannot show until a later milestone builds it. */
  readonly awaits: string | null;
}

export const BLOCK_CATALOG: readonly BlockCatalogEntry[] = [
  {
    type: "hero",
    label: "Hero",
    description: "A page's opening headline, with up to two actions and an image.",
    module: null,
    awaits: null,
  },
  {
    type: "service_times",
    label: "Service times",
    description: "When the church meets.",
    module: null,
    awaits: null,
  },
  {
    type: "locations",
    label: "Locations",
    description: "Where the church meets, with addresses and map links.",
    module: null,
    awaits: null,
  },
  {
    type: "mission",
    label: "Mission",
    description: "The church's mission statement and values.",
    module: null,
    awaits: null,
  },
  {
    type: "sermon_feature",
    label: "Sermon feature",
    description: "The latest sermon, a series, or one sermon.",
    module: "sermons_media",
    awaits: null,
  },
  {
    type: "event_listing",
    label: "Events",
    description: "Upcoming events, by category.",
    module: "events",
    awaits: null,
  },
  {
    type: "group_finder",
    label: "Group finder",
    description: "Public groups visitors can browse and filter.",
    module: "groups",
    awaits: null,
  },
  {
    type: "ministry_grid",
    label: "Ministries",
    description: "The church's ministries, each with a link.",
    module: null,
    awaits: null,
  },
  {
    type: "staff_grid",
    label: "Staff",
    description: "The people who lead the church, as the church presents them.",
    module: null,
    awaits: null,
  },
  {
    type: "giving",
    label: "Giving",
    description: "An invitation to give, linking to where gifts are taken.",
    module: null,
    awaits: null,
  },
  {
    type: "store_collection",
    label: "Store collection",
    description: "Products from one store collection.",
    module: "commerce",
    awaits: "commerce (Milestone 4)",
  },
  {
    type: "newsletter",
    label: "Newsletter sign-up",
    description: "A sign-up form for the church's emails.",
    module: null,
    awaits: null,
  },
  {
    type: "contact_form",
    label: "Contact form",
    description: "A form visitors send to the church.",
    module: null,
    awaits: null,
  },
  {
    type: "plan_visit",
    label: "Plan a visit",
    description: "What a first visit looks like, step by step.",
    module: null,
    awaits: null,
  },
  {
    type: "rich_text",
    label: "Text",
    description: "Paragraphs, headings, lists and quotes.",
    module: null,
    awaits: null,
  },
  {
    type: "faq",
    label: "Questions and answers",
    description: "Common questions, with answers.",
    module: null,
    awaits: null,
  },
  {
    type: "media_gallery",
    label: "Gallery",
    description: "A set of images with captions.",
    module: null,
    awaits: null,
  },
  {
    type: "custom_embed",
    label: "Embed",
    description: "A video, map or podcast from an approved provider.",
    module: null,
    awaits: null,
  },
  {
    type: "livestream",
    label: "Live now",
    description: "Your livestream at service times, and a replay link afterwards.",
    module: null,
    awaits: null,
  },
];

export const BLOCK_TYPES: readonly BlockType[] = BLOCK_CATALOG.map((entry) => entry.type);

/**
 * The variants a block type offers, in the order its contract lists them; the first is the default. Read from the
 * contract itself, so the theme conformance test (ADR 0050) walks every variant a block gains without being told.
 */
export function blockVariants(type: BlockType): readonly string[] {
  const contract = blockSchema.options.find((option) => option.shape.type.value === type);
  return contract === undefined ? [] : contract.shape.variant.unwrap().options;
}

// ------------------------------------------------------------------------------------------------ pages

export const pageDocumentSchema = z.object({
  /** Route slug; the home page is "home". */
  slug: z.string().min(1).max(120),
  title: z.string().min(1).max(160),
  description: z.string().max(300).optional(),
  status: z.enum(["draft", "published"]),
  blocks: z.array(blockSchema).min(1).max(40),
});

export type PageDocument = z.infer<typeof pageDocumentSchema>;

/** Renderers call this at the boundary so malformed content fails loudly, never as a half-rendered page. */
export function parsePageDocument(input: unknown): PageDocument {
  return pageDocumentSchema.parse(input);
}

/** Formats a 24 h HH:MM time for display; renderers share it so every theme prints the same text. */
export function formatServiceTime(time: string, locale = "en-US"): string {
  const [h, m] = time.split(":").map(Number);
  const date = new Date(Date.UTC(2026, 0, 4, h ?? 0, m ?? 0));
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
}

export const DAY_LABELS: Readonly<Record<ServiceTimesBlock["services"][number]["day"], string>> = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
};

/** The address a provider serves inside a frame, from the page address the church pasted. */
export function embedSource(provider: keyof typeof EMBED_PROVIDERS, url: string): string {
  switch (provider) {
    case "youtube": {
      const match = /(?:v=|youtu\.be\/)([A-Za-z0-9_-]{6,20})/.exec(url);
      return `https://www.youtube-nocookie.com/embed/${match?.[1] ?? ""}`;
    }
    case "vimeo": {
      const match = /vimeo\.com\/(\d{4,12})/.exec(url);
      return `https://player.vimeo.com/video/${match?.[1] ?? ""}`;
    }
    case "google_maps":
      return url;
    case "spotify":
      return url.replace("open.spotify.com/", "open.spotify.com/embed/");
  }
}
